import { PageController } from '@client/components/_core/layout/PageController';
import { PublicNoIndex } from '@client/components/_shared/public/PublicNoIndex';
import { trpcOptions } from '@client/trpc/client';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useState } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { InviteShell } from './InviteCard';
import { INVITE_LINK_VALIDATION_MUTATION_META } from './invite-link-validation';
import { InvalidInvite } from './steps/InvalidInvite';
import { InviteDetails } from './steps/InviteDetails';
import { LOAD_FAILED_MESSAGE, LoadFailed } from './steps/LoadFailed';
import { Loading } from './steps/Loading';
import { ProcessingSignUp } from './steps/ProcessingSignUp';
import { SignIn } from './steps/SignIn';
import { SignUpComplete } from './steps/SignUpComplete';
import { SignUpFailed } from './steps/SignUpFailed';
import { UserAlreadyExists } from './steps/UserAlreadyExists';
import { useStore } from './store';

export const Route = createFileRoute('/invite')({
    validateSearch: zodValidator(
        z.object({
            inviteSecret: z.string(),
            email: z.string(),
        }),
    ),
    component: InviteSignUp,
});

function InviteSignUp() {
    const { inviteSecret, email } = Route.useSearch();
    const currentStep = useStore((state) => state.currentStep);
    const { setInviteSecret, setCurrentStep, setInviteData, setError, setAllowedAuthMethods, setAllowNameUpdate } =
        useStore((state) => state.actions);
    // Bumped by "Try again" when the invitation could not be checked.
    const [attempt, setAttempt] = useState(0);
    const { mutate: validateInviteLink } = useMutation(
        trpcOptions.user.validateInviteLink.mutationOptions({
            meta: INVITE_LINK_VALIDATION_MUTATION_META,
        }),
    );

    useEffect(() => {
        // Set loading state
        setCurrentStep('loading');

        validateInviteLink(
            { inviteSecret },
            {
                onSuccess: (data) => {
                    setInviteSecret(inviteSecret);
                    setAllowedAuthMethods(data.allowedAuthMethods || []);
                    setAllowNameUpdate(data.allowNameUpdate ?? true);
                    setInviteData({
                        email: email,
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        inviteSecret,
                        organisationName: data.organisationName,
                        sessionId: data.sessionId,
                    });
                    setCurrentStep('inviteDetails');
                },
                onError: (error) => {
                    match(error.data?.errorId)
                        .with('invite.linkNotFound', () => setCurrentStep('invalidInvite'))
                        .with('invite.linkExpired', () => setCurrentStep('invalidInvite'))
                        .with('invite.userAlreadyExists', () => setCurrentStep('userAlreadyExists'))
                        .otherwise(() => {
                            setCurrentStep('loadFailed');
                            setError({ errorCode: 'unknown', errorMessage: LOAD_FAILED_MESSAGE });
                        });
                },
            },
        );
    }, [inviteSecret, attempt]);

    return (
        <PageController route={Route}>
            <PublicNoIndex />
            <InviteShell>
                {match(currentStep)
                    .with('loading', () => <Loading />)
                    .with('loadFailed', () => <LoadFailed onRetry={() => setAttempt((count) => count + 1)} />)
                    .with('inviteDetails', () => <InviteDetails />)
                    .with('signIn', () => <SignIn />)
                    .with('processingSignUp', () => <ProcessingSignUp />)
                    .with('signUpFailed', () => <SignUpFailed />)
                    .with('signUpComplete', () => <SignUpComplete />)
                    .with('invalidInvite', () => <InvalidInvite />)
                    .with('userAlreadyExists', () => <UserAlreadyExists />)
                    .exhaustive()}
            </InviteShell>
        </PageController>
    );
}
